import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, type ApiResponse } from '@/lib/api-client';

/**
 * Hooks for the contact-groups feature: group CRUD, membership, and the
 * group-scoped billing views (jobs, invoices, combined ledger, group invoice).
 */

export interface ContactGroupSummary {
  id: string;
  name: string;
  group_type: string | null;
  description: string | null;
  primary_contact_id: string | null;
  primary_contact_name: string | null;
  is_active: boolean;
  member_count: number;
  /** Whole-group client-portal access kill switch. */
  portal_access_blocked: boolean;
  /** Group-level emailed-MFA default (null = platform default of on). */
  portal_mfa_enabled: boolean | null;
  /** True when the group has at least one portal Manager member. */
  has_portal_manager?: boolean;
}

/** Filters accepted by the group list endpoint. */
export interface GroupListFilters {
  search?: string;
  group_type?: string;
  contact_id?: string;
  is_active?: boolean;
  portal_access_blocked?: boolean;
  portal_mfa?: 'on' | 'off' | 'default';
  has_billing_contact?: boolean;
  has_members?: boolean;
  has_portal_manager?: boolean;
  min_members?: number;
}

export interface GroupMember {
  id: string;
  group_id: string;
  contact_id: string;
  contact_name: string;
  email: string | null;
  contact_type: string;
  /** Relationship of this member within the group (relationship_types code). */
  relationship_type: string | null;
  /** Client-portal role in this group ('manager' | 'normal'), or null. */
  portal_role: 'manager' | 'normal' | null;
  /** Whether this member's contact has a portal user account. */
  has_portal_user: boolean;
  is_primary: boolean;
}

export interface ContactGroupDetail extends ContactGroupSummary {
  members: GroupMember[];
}

export interface GroupJob {
  id: string;
  title: string;
  job_code: string | null;
  status: string;
  client_id: string;
  client_name: string;
  budget_amount: number | null;
  /** True only if the job is on a NON-VOID invoice (voiding frees it again). */
  invoiced: boolean;
}

export interface GroupInvoice {
  id: string;
  invoice_number: string;
  client_id: string;
  client_name: string;
  status: string;
  total: number;
  outstanding_balance: number;
  issue_date: string;
  due_date: string;
}

export interface GroupLedger {
  members: Array<{ contact_id: string; contact_name: string; balance: number }>;
  entries: Array<{
    id: string;
    client_id: string;
    contact_name: string;
    entry_type: string;
    reference_type: string;
    amount: number;
    description: string | null;
    entry_date: string;
    group_running_balance: number;
  }>;
  summary: { total_debits: number; total_credits: number; balance: number };
}

export interface RelationshipTypeOption {
  code: string;
  label: string;
}

/** A group member related to a contact, with their existing (non-cancelled) jobs. */
export interface RelatedGroupMember {
  contact_id: string;
  contact_name: string;
  email: string | null;
  relationship_type: string | null;
  group_id: string;
  group_name: string;
  existing_jobs: Array<{
    id: string;
    title: string;
    job_code: string | null;
    job_type: string;
    status: string;
  }>;
}

export interface CreateGroupInput {
  name: string;
  group_type?: string | null;
  description?: string | null;
  primary_contact_id?: string | null;
  member_ids?: string[];
}

export interface UpdateGroupInput {
  name?: string;
  group_type?: string | null;
  description?: string | null;
  primary_contact_id?: string | null;
  is_active?: boolean;
}

export interface CreateGroupInvoiceInput {
  billing_entity_id: string;
  issue_date: string;
  due_date?: string | null;
  payment_terms_days?: number;
  notes?: string | null;
  service_type_code?: string | null;
  invoice_prefix?: string;
  job_ids?: string[];
  additional_line_items?: Array<{
    description: string;
    quantity: number;
    unit_price: number;
    tax_rate?: number;
  }>;
}

export const contactGroupKeys = {
  all: ['contact-groups'] as const,
  lists: () => [...contactGroupKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) => [...contactGroupKeys.lists(), params] as const,
  details: () => [...contactGroupKeys.all, 'detail'] as const,
  detail: (id: string) => [...contactGroupKeys.details(), id] as const,
  jobs: (id: string) => [...contactGroupKeys.detail(id), 'jobs'] as const,
  invoices: (id: string) => [...contactGroupKeys.detail(id), 'invoices'] as const,
  ledger: (id: string, filters?: Record<string, unknown>) =>
    [...contactGroupKeys.detail(id), 'ledger', filters ?? {}] as const,
};

/** Lists contact groups with optional detailed filters. */
export function useContactGroups(params: GroupListFilters = {}) {
  return useQuery({
    queryKey: contactGroupKeys.list(params as Record<string, unknown>),
    queryFn: async () => {
      const query = new URLSearchParams();
      if (params.search) query.set('search', params.search);
      if (params.group_type) query.set('group_type', params.group_type);
      if (params.contact_id) query.set('contact_id', params.contact_id);
      if (params.is_active !== undefined) query.set('is_active', String(params.is_active));
      if (params.portal_access_blocked !== undefined)
        query.set('portal_access_blocked', String(params.portal_access_blocked));
      if (params.portal_mfa) query.set('portal_mfa', params.portal_mfa);
      if (params.has_billing_contact !== undefined)
        query.set('has_billing_contact', String(params.has_billing_contact));
      if (params.has_members !== undefined) query.set('has_members', String(params.has_members));
      if (params.has_portal_manager !== undefined)
        query.set('has_portal_manager', String(params.has_portal_manager));
      if (params.min_members !== undefined) query.set('min_members', String(params.min_members));
      const res = await apiClient.get<
        ApiResponse<ContactGroupSummary[]> & {
          pagination: { page: number; pageSize: number; total: number };
        }
      >(`/contact-groups?${query.toString()}`);
      return { groups: res.data.data, total: res.data.pagination?.total ?? 0 };
    },
  });
}

/** Fetches a single group with members. */
export function useContactGroup(id: string | null) {
  return useQuery({
    queryKey: contactGroupKeys.detail(id ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<ContactGroupDetail>>(`/contact-groups/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

/** Jobs across group members. */
export function useGroupJobs(id: string | null) {
  return useQuery({
    queryKey: contactGroupKeys.jobs(id ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<GroupJob[]>>(`/contact-groups/${id}/jobs`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

/** Invoices across group members. */
export function useGroupInvoices(id: string | null) {
  return useQuery({
    queryKey: contactGroupKeys.invoices(id ?? ''),
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<GroupInvoice[]>>(
        `/contact-groups/${id}/invoices`,
      );
      return res.data.data;
    },
    enabled: !!id,
  });
}

/** Combined ledger for the group. */
export function useGroupLedger(
  id: string | null,
  filters: { entry_type?: string; date_from?: string; date_to?: string } = {},
) {
  return useQuery({
    queryKey: contactGroupKeys.ledger(id ?? '', filters),
    queryFn: async () => {
      const query = new URLSearchParams();
      if (filters.entry_type) query.set('entry_type', filters.entry_type);
      if (filters.date_from) query.set('date_from', filters.date_from);
      if (filters.date_to) query.set('date_to', filters.date_to);
      const res = await apiClient.get<ApiResponse<GroupLedger>>(
        `/contact-groups/${id}/ledger?${query.toString()}`,
      );
      return res.data.data;
    },
    enabled: !!id,
  });
}

/** Relationship types from setup data. */
export function useRelationshipTypes() {
  return useQuery({
    queryKey: ['system-lookups', 'relationship_types'],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<RelationshipTypeOption[]>>(
        '/system-lookups/relationship_types?is_active=true',
      );
      return res.data.data;
    },
  });
}

/** Group types from setup data (system lookups). */
export function useGroupTypes() {
  return useQuery({
    queryKey: ['system-lookups', 'group_types'],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<Array<{ code: string; label: string }>>>(
        '/system-lookups/group_types?is_active=true',
      );
      return res.data.data;
    },
  });
}

/**
 * Members of the group(s) a contact belongs to (excluding the contact itself),
 * each with their existing jobs. Used by the Job form to fan a job out to a
 * contact's group members and to show what work already exists.
 */
export function useRelatedGroupMembers(contactId: string | null | undefined) {
  return useQuery({
    queryKey: [...contactGroupKeys.all, 'related-members', contactId ?? ''],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<RelatedGroupMember[]>>(
        `/contact-groups/for-contact/${contactId}/members`,
      );
      return res.data.data;
    },
    enabled: !!contactId,
  });
}

export function useCreateContactGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGroupInput) => {
      const res = await apiClient.post<ApiResponse<ContactGroupSummary>>('/contact-groups', input);
      return res.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: contactGroupKeys.lists() }),
  });
}

export function useUpdateContactGroup(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateGroupInput) => {
      const res = await apiClient.put<ApiResponse<ContactGroupSummary>>(
        `/contact-groups/${id}`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactGroupKeys.detail(id) });
      qc.invalidateQueries({ queryKey: contactGroupKeys.lists() });
    },
  });
}

export function useDeleteContactGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/contact-groups/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: contactGroupKeys.lists() }),
  });
}

export function useAddGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      contact_id: string;
      relationship_type?: string | null;
      portal_role?: 'manager' | 'normal' | null;
    }) => {
      const res = await apiClient.post<ApiResponse<GroupMember>>(
        `/contact-groups/${groupId}/members`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactGroupKeys.detail(groupId) });
      qc.invalidateQueries({ queryKey: contactGroupKeys.lists() });
    },
  });
}

/** Sets a member's client-portal role ('manager' | 'normal' | null). */
export function useSetMemberPortalRole(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { contactId: string; portal_role: 'manager' | 'normal' | null }) => {
      await apiClient.patch(`/contact-groups/${groupId}/members/${input.contactId}/portal-role`, {
        portal_role: input.portal_role,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: contactGroupKeys.detail(groupId) }),
  });
}

/** Blocks or grants client-portal access for the whole group. */
export function useSetGroupPortalAccess(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (blocked: boolean) => {
      await apiClient.patch(`/contact-groups/${groupId}/portal-access`, { blocked });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactGroupKeys.detail(groupId) });
      qc.invalidateQueries({ queryKey: contactGroupKeys.lists() });
    },
  });
}

/** Sets the group-level emailed-MFA default (null = platform default). */
export function useSetGroupPortalMfa(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enabled: boolean | null) => {
      await apiClient.patch(`/contact-groups/${groupId}/portal-mfa`, { enabled });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactGroupKeys.detail(groupId) });
      qc.invalidateQueries({ queryKey: contactGroupKeys.lists() });
    },
  });
}

/**
 * Grants client-portal access to a group member's contact by creating a portal
 * user and sending an invitation email (or reactivating + re-inviting if a
 * suspended user already exists). Delegates to the canonical
 * `POST /contacts/:contactId/assign-portal` endpoint. On success the group
 * detail is refreshed so the member's `has_portal_user` flips to true.
 */
export function useGrantPortalAccess(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: string) => {
      const res = await apiClient.post<{
        success: boolean;
        data: { portalUserId: string; email: string; status: string };
        message?: string;
      }>(`/contacts/${contactId}/assign-portal`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactGroupKeys.detail(groupId) });
      qc.invalidateQueries({ queryKey: contactGroupKeys.lists() });
    },
  });
}

export function useRemoveGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (contactId: string) => {
      await apiClient.delete(`/contact-groups/${groupId}/members/${contactId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactGroupKeys.detail(groupId) });
      qc.invalidateQueries({ queryKey: contactGroupKeys.lists() });
    },
  });
}

export function useCreateGroupInvoice(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateGroupInvoiceInput) => {
      const res = await apiClient.post<ApiResponse<{ id: string; invoice_number: string }>>(
        `/contact-groups/${groupId}/invoices`,
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: contactGroupKeys.invoices(groupId) });
      qc.invalidateQueries({ queryKey: contactGroupKeys.jobs(groupId) });
      qc.invalidateQueries({ queryKey: contactGroupKeys.ledger(groupId) });
    },
  });
}
